import { useEffect, useRef, useState } from "react";
import ReactQuill from "react-quill";
import {
  LuCode,
  LuEye,
  LuPenTool,
  LuTable,
  LuSparkles,
  LuImage,
  LuCircleHelp,
  LuMegaphone,
  LuInfo,
  LuChevronDown,
  LuChevronUp,
} from "react-icons/lu";
import TableBuilderModal from "./TableBuilderModal";
import McqBuilderModal from "./McqBuilderModal";

const TEMPLATES = {
  callout: `
<div class="mt-6 border-l-4 border-primary bg-secondary p-5 rounded-r-xl">
  <h3 class="font-heading text-lg font-bold text-primary">Important Note</h3>
  <p class="mt-2 leading-7 text-secondary-foreground">UPSC rank requirements and service cut-offs can change every year depending on vacancies, candidate preferences, category-wise allocation and the final merit list.</p>
</div>
`,
  blueBanner: `
<div class="my-12 ad-banner" role="complementary" aria-label="Achiwon preparation advertisement">
  <div class="ad-copy">
    <div class="ad-label">Achiwon Preparation</div>
    <h3>Prepare Smarter for UPSC 2026</h3>
    <p>Practice current affairs, previous year questions and exam-focused resources in one structured preparation journey.</p>
  </div>
  <a href="/free-practice">Explore Resources →</a>
</div>
`,
  orangeBanner: `
<div class="my-12 ad-banner" style="background:linear-gradient(135deg,#F48224,#f59e0b)" role="complementary" aria-label="Achiwon test series advertisement">
  <div class="ad-copy">
    <div class="ad-label">Daily Practice</div>
    <h3>Test Your UPSC Preparation</h3>
    <p>Challenge yourself with exam-style practice and build speed, accuracy and confidence.</p>
  </div>
  <a href="#achiwon-quiz">Take MCQ Test →</a>
</div>
`,
  imageCard: `
<div class="mt-8 content-image-card">
  <img src="https://uxmagic.blob.core.windows.net/public/agent-images/upsc-careers-1790648910818-kzugvrwrhm.png" alt="UPSC civil services career planning and preparation">
  <div class="image-caption">Visual guide: Keep rank trends in context while planning your UPSC preparation and service preferences.</div>
</div>
`,
};

const DescriptionEditor = ({ value, onChange, readOnly }) => {
  const quillRef = useRef(null);
  const [localValue, setLocalValue] = useState(value || "");
  const [activeTab, setActiveTab] = useState("visual"); // visual | html | preview
  const [showTableBuilder, setShowTableBuilder] = useState(false);
  const [showMcqBuilder, setShowMcqBuilder] = useState(false);
  const [showGuide, setShowGuide] = useState(false);

  useEffect(() => {
    setLocalValue(value || "");
  }, [value]);

  const handleChange = (content) => {
    setLocalValue(content);
    onChange(content);
  };

  const insertSnippet = (snippet) => {
    if (!snippet) return;
    const updated = (localValue || "") + "\n" + snippet;
    handleChange(updated);
  };

  const toolbarOptions = [
    ["bold", "italic", "underline", "strike"],
    ["blockquote", "code-block"],
    ["link", "image", "video"],
    [{ header: 1 }, { header: 2 }, { header: 3 }],
    [{ list: "ordered" }, { list: "bullet" }],
    [{ color: [] }, { background: [] }],
    [{ align: [] }],
    ["clean"],
  ];

  // Preview tab quiz evaluation
  const handlePreviewSubmit = (e) => {
    const quiz = document.querySelector("#preview-tab-box #achiwon-quiz, #preview-tab-box .mcq-box");
    if (!quiz) return;
    const questions = quiz.querySelectorAll("[data-answer]");
    let score = 0;
    const total = questions.length;
    questions.forEach((q) => {
      const selected = q.querySelector("input:checked");
      if (selected && selected.value === q.dataset.answer) score++;
    });
    const result = quiz.querySelector(".mcq-result");
    if (result) {
      result.textContent = `Your Score: ${score} / ${total} — ${
        score === total ? "Excellent! 🎉" : "Good job! Keep practising."
      }`;
      result.removeAttribute("hidden");
      result.style.display = "block";
    }
  };

  return (
    <div className="border border-border rounded-2xl overflow-hidden bg-card shadow-xs">
      {/* Top Header with Mode Tabs & Builders */}
      <div className="bg-muted px-4 py-3 border-b border-border space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-3">
          {/* Editor Mode Tabs */}
          <div className="flex items-center gap-1 bg-card rounded-xl p-1 border border-border">
            <button
              type="button"
              onClick={() => setActiveTab("visual")}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${
                activeTab === "visual"
                  ? "bg-primary text-white shadow-xs"
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              <LuPenTool className="text-sm" /> Visual Editor
            </button>
            <button
              type="button"
              onClick={() => setActiveTab("html")}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${
                activeTab === "html"
                  ? "bg-primary text-white shadow-xs"
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              <LuCode className="text-sm" /> HTML Source
            </button>
            <button
              type="button"
              onClick={() => setActiveTab("preview")}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${
                activeTab === "preview"
                  ? "bg-primary text-white shadow-xs"
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              <LuEye className="text-sm" /> In-Editor Preview
            </button>
          </div>

          {/* Dedicated Dynamic Builders */}
          <div className="flex items-center gap-2 flex-wrap">
            <button
              type="button"
              onClick={() => setShowTableBuilder(true)}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#114598] hover:bg-[#F68122] text-white rounded-xl text-xs font-bold transition-all shadow-xs cursor-pointer"
              title="Add a custom table with any number of rows & columns"
            >
              <LuTable className="text-sm" />
              <span>📊 Table Builder (Rows &amp; Columns)</span>
            </button>

            <button
              type="button"
              onClick={() => setShowMcqBuilder(true)}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-emerald-700 hover:bg-emerald-600 text-white rounded-xl text-xs font-bold transition-all shadow-xs cursor-pointer"
              title="Add an interactive MCQ quiz with automated grading"
            >
              <LuCircleHelp className="text-sm" />
              <span>❓ MCQ Quiz Builder</span>
            </button>

            <button
              type="button"
              onClick={() => setShowGuide(!showGuide)}
              className="inline-flex items-center gap-1 px-2.5 py-1.5 bg-card border border-border hover:border-primary text-muted-foreground hover:text-primary rounded-xl text-xs font-semibold transition-colors cursor-pointer"
            >
              <LuInfo className="text-sm text-primary" />
              <span>Guide (કેવી રીતે વાપરવું)</span>
              {showGuide ? <LuChevronUp /> : <LuChevronDown />}
            </button>
          </div>
        </div>

        {/* Quick Insert Preset Snippets */}
        <div className="flex items-center gap-2 flex-wrap text-xs pt-1 border-t border-border/60">
          <span className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
            + Quick Blocks:
          </span>
          <button
            type="button"
            onClick={() => insertSnippet(TEMPLATES.callout)}
            className="inline-flex items-center gap-1 px-2.5 py-1 bg-card border border-border rounded-lg hover:border-primary hover:text-primary transition-colors text-xs font-medium cursor-pointer"
            title="Insert Highlight Note Box"
          >
            <LuSparkles className="text-amber-500" /> Callout / Note
          </button>
          <button
            type="button"
            onClick={() => insertSnippet(TEMPLATES.blueBanner)}
            className="inline-flex items-center gap-1 px-2.5 py-1 bg-card border border-border rounded-lg hover:border-primary hover:text-primary transition-colors text-xs font-medium cursor-pointer"
            title="Insert Blue CTA Banner"
          >
            <LuMegaphone className="text-[#114598]" /> Blue Banner
          </button>
          <button
            type="button"
            onClick={() => insertSnippet(TEMPLATES.orangeBanner)}
            className="inline-flex items-center gap-1 px-2.5 py-1 bg-card border border-border rounded-lg hover:border-primary hover:text-primary transition-colors text-xs font-medium cursor-pointer"
            title="Insert Orange CTA Banner"
          >
            <LuMegaphone className="text-[#F68122]" /> Orange Banner
          </button>
          <button
            type="button"
            onClick={() => insertSnippet(TEMPLATES.imageCard)}
            className="inline-flex items-center gap-1 px-2.5 py-1 bg-card border border-border rounded-lg hover:border-primary hover:text-primary transition-colors text-xs font-medium cursor-pointer"
            title="Insert Image Card with Caption"
          >
            <LuImage className="text-blue-500" /> Image Card
          </button>
        </div>
      </div>

      {/* Expandable User Guide (Gujarati + English) */}
      {showGuide && (
        <div className="bg-blue-50/70 border-b border-blue-200 px-5 py-4 text-xs text-slate-700 leading-relaxed space-y-2">
          <p className="font-bold text-[#114598] text-sm flex items-center gap-1.5">
            <LuInfo /> Developer Panel Blog Features Guide (આ કેવી રીતે વાપરવું):
          </p>
          <ul className="list-disc pl-5 space-y-1.5">
            <li>
              <strong>📊 Table Builder (તમારા મનગમતા Rows &amp; Columns):</strong> ઉપરોક્ત બટન પર ક્લિક કરો. તમે 2, 3, 4 કે તેથી વધુ Columns અને ગમે તેટલા Rows ઉમેરી શકો છો. સીધું Grid માં Header અને Content લખી &quot;Insert Table into Blog&quot; દબાવો. કોઈ HTML કોડિંગની જરૂર નથી!
            </li>
            <li>
              <strong>❓ MCQ Quiz Builder:</strong> આ બટનથી તમે પ્રશ્ન અને A, B, C, D ઓપ્શન સેટ કરી શકો છો અને કયો જવાબ સાચો છે તે રેડિયો બટન સિલેક્ટ કરો. Landing page પર વિદ્યાર્થીઓ ટેસ્ટ આપી શકશે અને સ્કોર આપોઆપ ગણાશે.
            </li>
            <li>
              <strong>👁️ Full Page Live Preview:</strong> પેજમાં ઉપર અને નીચે &quot;Full Page Live Preview&quot; બટન છે. તે દબાવવાથી landing page પર Header અને Footer ની વચ્ચે Table of Contents, Tables અને Quizzes કેવા દેખાશે તેનો લાઈવ ડેમો જોઈ શકાય છે.
            </li>
            <li>
              <strong>⚡ Marketing Slug:</strong> ટાઇટલ લખ્યા પછી &quot;Generate from Title&quot; પર ક્લિક કરવાથી ક્લીન SEO URL (જેમ કે <code>/blog/ranks-in-upsc-requirements...</code>) આપોઆપ બની જશે.
            </li>
          </ul>
        </div>
      )}

      {/* Editor Content Body */}
      <div className="p-3">
        {activeTab === "visual" && (
          <ReactQuill
            ref={quillRef}
            theme="snow"
            value={localValue}
            onChange={handleChange}
            readOnly={readOnly}
            modules={{ toolbar: toolbarOptions }}
            placeholder="Write blog description or use Table Builder / MCQ Builder above..."
          />
        )}

        {activeTab === "html" && (
          <div className="relative">
            <textarea
              value={localValue}
              onChange={(e) => handleChange(e.target.value)}
              rows={18}
              readOnly={readOnly}
              className="w-full font-mono text-xs p-4 rounded-xl border border-border bg-slate-900 text-emerald-400 focus:outline-none focus:ring-2 focus:ring-primary leading-relaxed"
              placeholder="Paste or edit HTML source code directly here..."
            />
            <div className="absolute top-2 right-2 text-[10px] bg-slate-800 text-slate-400 px-2 py-0.5 rounded">
              HTML Mode
            </div>
          </div>
        )}

        {activeTab === "preview" && (
          <div
            id="preview-tab-box"
            className="border border-border rounded-xl p-6 bg-white min-h-[300px] max-h-[600px] overflow-y-auto"
            onClick={(e) => {
              if (e.target && e.target.tagName === "BUTTON") {
                if (
                  e.target.textContent &&
                  e.target.textContent.toLowerCase().includes("submit")
                ) {
                  e.preventDefault();
                  handlePreviewSubmit();
                }
              }
            }}
          >
            <p className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground mb-4 pb-2 border-b border-border">
              Editor In-line Preview:
            </p>
            <div
              className="blog-content leading-relaxed"
              dangerouslySetInnerHTML={{
                __html: localValue || "<p class='text-gray-400'>No content to preview.</p>",
              }}
            />
          </div>
        )}
      </div>

      {/* Table Builder Modal */}
      <TableBuilderModal
        isOpen={showTableBuilder}
        onClose={() => setShowTableBuilder(false)}
        onInsert={(html) => insertSnippet(html)}
      />

      {/* MCQ Builder Modal */}
      <McqBuilderModal
        isOpen={showMcqBuilder}
        onClose={() => setShowMcqBuilder(false)}
        onInsert={(html) => insertSnippet(html)}
      />
    </div>
  );
};

export default DescriptionEditor;
